<template>
  <v-container class="d-flex flex-wrap align-center pa-0 filters-container">
    <slot name="toggle-filter:extended" />
    <DpplTooltip
      v-if="showMyJobs && enforcedMyJobsEnabled"
      tooltip-placement="bottom"
    >
      <template #activator="{ onMouseenter, onMouseleave }">
        <div
          @mouseenter="onMouseenter"
          @mouseleave="onMouseleave"
        >
          <MyJobsSwitch
            is-disabled
            class="mr-10"
            @filters-tracking-event="$emit('filters-tracking-event', $event)"
          />
        </div>
      </template>
      <template #tooltip-content>
        You are only allowed to see jobs that are your own
      </template>
    </DpplTooltip>
    <MyJobsSwitch
      v-else-if="showMyJobs"
      :is-disabled="modulePageLoading"
      class="mr-4"
      @filters-tracking-event="$emit('filters-tracking-event', $event)"
    />
    <SearchButton
      :is-disabled="modulePageLoading"
      class="chip--hover"
    />
    <v-menu
      v-for="(field, index) in tempFilters"
      :key="field.id"
      v-model="menus[index]"
      :close-on-content-click="false"
      max-width="455"
      location="bottom"
    >
      <template #activator="{ props: menu }">
        <v-menu
          open-on-hover
          max-width="100%"
          open-delay="200"
          location="bottom"
        >
          <template #activator="{ props: hover }">
            <div
              v-show="!hide && (Boolean(fields[index].value.length) || field.alwaysOn)"
            >
              <DpplChip
                v-bind="menu"
                :class="[
                  hover && fields[index].value.length ? 'chip--hover' : '',
                  'filter-chip',
                ]"
                arrow
                :is-active="menus[index]"
                :disabled="modulePageLoading"
                :chip-height="32"
                chip-color="white"
                role="combobox"
                :aria-label="field.label"
                reduce-font-size
              >
                <template #label>
                  {{ field.label }}
                </template>
                <v-hover v-slot="{ isHovering: closeMenu, props }">
                  <v-btn
                    v-show="fields[index].value.length"
                    v-bind="props"
                    elevation="0"
                    variant="flat"
                    width="16"
                    height="16"
                    icon
                    :class="['filter-btn ml-2',
                             menus[index]
                               ? 'text-brandPrimary'
                               : 'text-white']"
                    :color="menus[index]
                      ? 'white'
                      : (closeMenu ? 'orange500' : 'success')"
                    @click.stop="clearFilters({ index, router: $router })"
                  >
                    <span
                      v-if="!closeMenu"
                      class="active-filter-number"
                    >
                      {{ fields[index].value.length }}
                    </span>
                    <IconCloseSmall v-show="closeMenu" />
                  </v-btn>
                </v-hover>
              </DpplChip>
            </div>
          </template>
          <FilterHoverList :field="fields[index]" />
        </v-menu>
      </template>

      <FilterMenuCard
        :field="field"
        :is-open="menus[index]"
        @set-to-applied-filters="resetValues({ fieldIndex: index, setToAppliedFilters: true })"
        @set-temp-filters="setTempFilters({ value: $event, index })"
        @apply-filters="applyFilters(index);"
        @add-snackbar="addErrorSnackbar"
      />
    </v-menu>
    <div v-show="!hide">
      <v-btn
        v-if="noActiveFilters"
        icon
        size="small"
        variant="flat"
        color="white"
        class="filter-modal-btn"
        :disabled="modulePageLoading"
        height="32"
        width="32"
        aria-label="More filters"
        @click="setModal({ field: 'filter', value: true })"
      >
        <IconFilters class="icon-filters" />
      </v-btn>
      <DpplChip
        v-else
        class="filter-chip chip--hover"
        :disabled="modulePageLoading"
        :chip-height="32"
        chip-color="white"
        reduce-font-size
        @click="setModal({ field: 'filter', value: true })"
      >
        Filters
        <v-icon
          size="16"
          class="pl-1"
          icon="$plus"
        />
      </DpplChip>
    </div>
    <FilterModal
      @apply-filters="applyFilters"
      @select-all="selectAll($event)"
      @set-temp-filters="setTempFilters($event)"
      @close="
        setModal({ field: 'filter', value: false });
        resetValues({ setToAppliedFilters: true });
      "
      @clear="clearFilters({ router: $router })"
    />
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useModals } from '@/stores/modals/modals';
import { useFilters } from '@/stores/filters/filters';
import DpplChip from '@/components/datapeople/DpplChip.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import MyJobsSwitch from '@/components/filter/filterMenu/MyJobsSwitch.vue';
import FilterModal from '@/components/filter/filterMenu/FilterModal.vue';
import FilterHoverList from '@/components/filter/filterMenu/FilterHoverList.vue';
import FilterMenuCard from '@/components/filter/filterMenu/FilterMenuCard.vue';
import SearchButton from '@/components/filter/filterMenu/SearchButton.vue';
import { useAppSnackbar } from '@/stores/app/snackbar';
import IconCloseSmall from '@/assets/icon-close-small.svg?component';
import IconFilters from '@/assets/icon-filters.svg?component';

defineProps({
  modulePageLoading: { type: Boolean, default: false },
  hide: { type: Boolean, default: false },
  showMyJobs: { type: Boolean, default: true },
});

const emits = defineEmits(['filters-tracking-event']);

// Router
const route = useRoute();
const router = useRouter();

// Stores
const filtersStore = useFilters();
const {
  fields,
  tempFilters,
  enforcedMyJobsEnabled,
  noActiveFilters,
  trackingFilters,
} = storeToRefs(filtersStore);
const {
  resetValues,
  setTempFilters,
  clearValues,
  sendFilterRequest,
} = filtersStore;

const { setModal } = useModals();

const { addErrorSnackbar } = useAppSnackbar();

// Reactive Data
const menus = ref([]);

// Hooks
onMounted(() => {
  fields.value.forEach((_, index) => {
    menus.value[index] = false;
  });
});

// Methods
const clearFilters = ({ index, router: routerFromState }) => {
  emits('filters-tracking-event', {
    action: 'filterClear',
    args: {
      filter: fields.value[index]?.label || 'all',
      page: route.name,
    },
  });
  clearValues({ index, router: routerFromState });
};

const applyFilters = (menuIndex) => {
  if (menuIndex !== undefined) {
    menus.value = menus.value.map(() => false);
  }

  emits('filters-tracking-event', {
    action: 'filterApply',
    args: { filters: trackingFilters.value, page: route.name },
  });

  // New Events in testing
  emits('filters-tracking-event', {
    action: 'appliedFilters',
    args: { page: route.name, filterTypes: trackingFilters.value },
  });
  sendFilterRequest({ router, resetData: true });
};
</script>

<style scoped lang="less">
.filters-container {
  gap: 8px;
}

.active-filter-number {
  font-size: 10px;
}

.filter-btn {
  text-indent: 0;
}

.filter-modal-btn {
  :deep(.v-btn__overlay) {
    display: none;
  }

  &.v-btn.v-btn--disabled {
    opacity: 0.3;
  }

  &.v-btn:hover {
    background: @bg-brand-default !important;

    :deep(svg path) {
      fill: @white !important;
    }
  }
}

.filter-chip {
  &:hover {
    background: @bg-brand-default !important;
    color: @white !important;
  }

  &:deep(.v-chip__overlay) {
    display: none;
  }
}
</style>
